import {
	BarChart3,
	Boxes,
	CheckCircle2,
	Clock3,
	CreditCard,
	FileText,
	Grid2x2,
	Home,
	Sparkles,
	Users,
	Wallet,
} from "lucide-react";
import type { ComponentType } from "react";

import { getCustomizationValue } from "src/lib/utils";
import type { AuthUser, PermissionCheck } from "src/types/auth";

export interface NavChild {
	path: string;
	name: string;
	permission?: PermissionCheck;
}

export interface NavItem {
	path?: string;
	name: string;
	icon: ComponentType<{ className?: string }>;
	permission?: PermissionCheck;
	/** Renders a lock and disables navigation — used for not-yet-released modules. */
	locked?: boolean;
	children?: NavChild[];
}

export interface NavGroup {
	/** Group heading; omitted for the ungrouped first item (Home). */
	label?: string;
	items: NavItem[];
}

/**
 * Sidebar structure, following the prototype's grouping (Business / Finance / Operations).
 *
 * Deviation from the prototype: it has a flat nav with no sub-items, but this app has
 * secondary routes (invoice groups, inventory logs/requests, sub-accounts) that would be
 * stranded if dropped. They are kept as an indented sub-list styled to match.
 */
export const buildNavGroups = (user: AuthUser | null): NavGroup[] => {
	// getCustomizationValue returns "" when the tenant has no override, so each label
	// falls back to the prototype's wording.
	const customerLabel = getCustomizationValue(user, "customer_label") || "Customers";
	const invoiceLabel = getCustomizationValue(user, "invoice_label") || "Invoices";
	const invoiceGroupLabel = getCustomizationValue(user, "invoice_group_label") || "Invoice Groups";
	const inventoryLabel = getCustomizationValue(user, "inventory_label", true) || "Inventory";
	const subAccountLabel = getCustomizationValue(user, "sub_account_label", true) || "Sub Accounts";
	const isSchool = user?.organization_type_name === "Schools";

	return [
		{
			items: [{ path: "/dashboard", name: "Home", icon: Home }],
		},
		{
			label: "Business",
			items: [
				{
					path: "/customers",
					name: customerLabel,
					icon: Users,
					...(isSchool
						? {
								children: [
									{ path: "/customers", name: customerLabel },
									{ path: "/sub-accounts", name: subAccountLabel },
								],
							}
						: {}),
				},
				{
					path: "/invoices",
					name: invoiceLabel,
					icon: FileText,
					children: [
						{ path: "/invoices", name: invoiceLabel, permission: "view_invoices" },
						{ path: "/invoice-groups", name: invoiceGroupLabel },
					],
				},
				{
					path: "/inventory",
					name: inventoryLabel,
					icon: Boxes,
					children: [
						{ path: "/inventory", name: "Items" },
						{ path: "/inventory/logs", name: `${inventoryLabel} Logs` },
						{ path: "/inventory/requests", name: `${inventoryLabel} Requests`, permission: "view_store_requests" },
					],
				},
				{ path: "/payments", name: "Payments", icon: CreditCard, permission: "view_payment" },
			],
		},
		{
			label: "Finance",
			items: [
				{ path: "/wallet", name: "Wallet", icon: Wallet, permission: "view_payment" },
				{ path: "/accounts", name: "Accounts", icon: BarChart3 },
				{ path: "/settlements", name: "Settlements", icon: CheckCircle2, permission: "view_settlements" },
				{ path: "/reconciliation", name: "Reconciliation", icon: Clock3, permission: "view_reconciliation" },
			],
		},
		{
			label: "Operations",
			items: [
				{ name: "Expense Tracker", icon: Sparkles, locked: true },
				{ path: "/payouts", name: "Payouts", icon: Grid2x2, locked: true },
			],
		},
	];
};
